// @spec SHL-STR-022
'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { TranslateFn } from '@/lib/i18n';
import { formatKeyCombo } from '@/lib/keyboard-shortcuts';
import { cn } from '@/lib/utils';

export interface ShortcutKeyProps {
  /** Key combination string (e.g., "ctrl+k", "shift+/") or array of alternatives */
  keys: string | string[];
  /** Optional className override */
  className?: string;
}

/**
 * Component to render keyboard shortcut key combinations with styled `<kbd>` elements.
 *
 * Features:
 * - Parses key combination strings
 * - Shows OS-appropriate symbols (⌘ on Mac, Ctrl on other platforms)
 * - Renders each key in a separate styled kbd element
 * - Shows multiple alternatives with "or" separator
 *
 * @example
 * <ShortcutKey keys="ctrl+k" />
 * // Renders: ⌘ K (on Mac) or Ctrl K (on other platforms)
 *
 * @example
 * <ShortcutKey keys={["ctrl+k", "ctrl+/"]} />
 * // Renders: ⌘ K or ⌘ / (on Mac)
 */
export function ShortcutKey({ keys, className }: ShortcutKeyProps) {
  const { t } = useTranslation('common');
  const translate: TranslateFn = t;
  const keysList = Array.isArray(keys) ? keys : [keys];
  const orLabel = translate('shortcuts.or');

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {keysList.map((keyCombo, index) => (
        <React.Fragment key={keyCombo}>
          {index > 0 && <span className="mx-1 text-xs text-muted-foreground">{orLabel}</span>}
          <KeyCombo keys={keyCombo} />
        </React.Fragment>
      ))}
    </span>
  );
}

interface KeyComboProps {
  keys: string;
}

/**
 * Renders a single key combination (e.g., "ctrl+k").
 */
function KeyCombo({ keys }: KeyComboProps) {
  const parts = formatKeyCombo(keys);

  return (
    <span className="inline-flex items-center gap-0.5">
      {parts.map((part, index) => (
        <kbd
          key={`${part}-${String(index)}`}
          className={cn(
            'inline-flex h-5 min-w-5 items-center justify-center rounded border px-1.5',
            'bg-muted text-[11px] font-medium text-muted-foreground',
            'border-border/50 shadow-[0_1px_0_1px_rgba(0,0,0,0.05)]',
            'dark:shadow-[0_1px_0_1px_rgba(255,255,255,0.05)]'
          )}
          aria-label={part}
        >
          {part}
        </kbd>
      ))}
    </span>
  );
}
